import DOMPurify from "dompurify";
import {
  FormElement, Instance, Validator, ValueComponent, ValueElement, Validation
} from "./components/Base";
import LabelElement from "./components/Label";
import RequiredDecorator from "./components/decorators/labels/RequiredDecorator";
import TooltipDecorator from "./components/decorators/labels/TooltipDecorator";
import { TextInputElement, TextInputType } from "./components/TextInput"
import { translate } from "./Helpers";
import SelectElement from "./components/Select";
import NumericDateElement from "./components/NumberDate";
import CheckboxElement from "./components/Checkbox";
import MarketingConsentDecorator from "./components/decorators/checkboxes/MarketingConsentDecorator";
import HideCheckboxDecorator from "./components/decorators/checkboxes/HideCheckboxDecorator";
import RecaptchaFactory from "./components/RecaptchaFactory";
import ButtonElement from "./components/Button";
import ValidationElement from "./components/Validation";
import RequiredValidator from "./components/validators/RequiredValidator";
import MaxLengthValidator from "./components/validators/MaxLengthValidator";
import MinLengthValidator from "./components/validators/MinLengthValidator";
import MatchValidator from "./components/validators/MatchValidator";
import FormatValidator from "./components/validators/FormatValidator";
import BirthDateValidator from "./components/validators/BirthDateValidator";
import MaskDecorator from "./components/decorators/textInputs/MaskDecorator";
import AbroadConsentDecorator from "./components/decorators/checkboxes/AbroadConsentDecorator";
import AbroadConsentConfirmDecorator from "./components/decorators/checkboxes/AbroadConsentConfirmDecorator";
import CESAppInfo from "./CESAppInfo";

const TEXT_FIELD_REGEX = /^(?!.*(?:[<>])).*?$/;

const applyDecorators = (instance: Instance, decorator: any) => {
  if (decorator.canApply) {
    return decorator.apply(instance)
  }

  return instance;
}

const createValidation = (rows: {
  valueElement: ValueElement,
  field: any
}[], formId: string, _formName: string): Validation => {
  const getValidators = (valueElement: ValueElement, field: any) => {
    const allValidators: any = [{
      canAdd: field.validation.required,
      add: () => new RequiredValidator(`${formId}-${valueElement.name}-required`,
        translate(`field.${valueElement.name}.validation.required`))
    }, {
      canAdd: field.validation.maxLength,
      add: () => new MaxLengthValidator(`${formId}-${valueElement.name}-maxLength`,
        translate(`field.${valueElement.name}.validation.maxLength`),
        field.validation.maxLength)
    }, {
      canAdd: field.validation.minLength,
      add: () => new MinLengthValidator(`${formId}-${valueElement.name}-minLength`,
        translate(`field.${valueElement.name}.validation.minLength`),
        field.validation.minLength)
    }, {
      canAdd: field.validation.passwordValidation,
      add: () => new FormatValidator(`${formId}-${valueElement.name}-passwordValidation`,
        translate(`field.${valueElement.name}.validation.passwordValidation`),
        field.validation.passwordValidation)
    }, {
      canAdd: field.validation.match,
      add: () => new MatchValidator(`${formId}-${valueElement.name}-match`,
        translate(`field.${valueElement.name}.validation.match`),
        field.validation.match)
    }, {
      canAdd: field.validation.format && field.validation.format === "email",
      add: () => new FormatValidator(`${formId}-${valueElement.name}-format`,
        translate(`field.${valueElement.name}.validation.format`),
        field.validation.emailValidation)
    }, {
      canAdd: field.type === "dateselect" && field.name === "birthdate",
      add: () => new BirthDateValidator(`${formId}-${valueElement.name}-isDate`,
        translate(`field.${valueElement.name}.validation.isDate`),
        field.validation.format,
        field.validation.minYear)
    }, {
      canAdd: field.validation.numberValidation,
      add: () => new FormatValidator(`${formId}-${valueElement.name}-numberValidation`,
        translate(`field.${valueElement.name}.validation.numberValidation`),
        field.validation.numberValidation)
    }, {
      canAdd: field.validation.format && field.type === "text" && field.validation.format !== "email",
      add: () => new FormatValidator(`${formId}-${valueElement.name}-format`,
        translate(`generic.invalid.format.message`),
        field.validation.format)
    },
    {
      canAdd: field.validation.pattern,
      add: () =>
        new FormatValidator(
          `${formId}-${valueElement.name}-pattern`,
          translate(`generic.invalid.format.message`),
          field.validation.pattern
        ),
    }];

    return allValidators.reduce((result: Validator[], validator: any) => {
      if (validator.canAdd) {
        result.push(validator.add());
      }

      return result;
    }, []);
  }

  const validationRows = rows.map((row) => {
    const validators = getValidators(row.valueElement, row.field);

    if (row.field.validation.readOnly) {
      row.valueElement.disable();
    }

    return {
      valueElement: row.valueElement,
      validators
    }
  });

  return new ValidationElement(validationRows, ["validation-error"]);
}

const createLabel = (field: any, classes: string[] = []) => {
  const hasTooltip = false; //field.tip && field.tip !== "";
  const labelClasses = hasTooltip ? [...classes, "ces-form-tooltip"] : classes;

  const labelElement: Instance = new LabelElement(
    field.name,
    translate(`field.${field.name}.label`),
    labelClasses
  );

  const decorators: any = [
    {
      canApply: hasTooltip,
      apply: (label: Instance) => new TooltipDecorator(label, field.tip, ["ces-form-tooltiptext"])
    },
    {
      canApply: field.validation.required && field.name !== "termsAndConditions",
      apply: (label: Instance) => new RequiredDecorator(label, " *", ["req"])
    },
  ];

  return decorators.reduce(applyDecorators, labelElement);
}

const createInput = (field: any, type: TextInputType, formId: string, formName: string): FormElement => {
  const getUserValue = () => field.value || ""
  const controls: ValueComponent[] = []
  const rows: {
    valueElement: ValueElement,
    field: any
  }[] = [];
  const container = document.createElement("div");
  container.classList.add(...["input-container"]);

  if (field.prefixField) {
    container.classList.add("row");
    const prefixControlCreators: Record<string, (f: any, prefix?: boolean) => any> = { select: selectControl };
    const createPrefixControl = prefixControlCreators[field.prefixField.type];
    if (!createPrefixControl) throw new Error(`Unknown prefix field type: ${field.prefixField.type}`);
    const prefixControl = createPrefixControl(field.prefixField, true);
    container.appendChild(prefixControl.element);
    controls.push(prefixControl);
    rows.push({ valueElement: prefixControl, field: field.prefixField });
  }

  const { element: label } = createLabel(field, ["label-visible"]);
  container.appendChild(label);

  const placeholder = { key: "placeholder", value: translate(`field.${field.name}.placeholder`) };
  const customAttrs = field.customAttrs ? [...field.customAttrs, placeholder] : [placeholder]

  const control = new TextInputElement(field.name,
    getUserValue(),
    type,
    field.classList
      || field.prefixField ? ["form-field", "dob", "eight", "columns"] : ["form-field"],
    customAttrs);


  const maskValue = translate(`masks.${field.schemaAttribute}`, false);

  const decorators: any = [{
    canApply: maskValue !== "",
    apply: (element: Instance) => {
      const maskedControl = new MaskDecorator(element, maskValue);
      return maskedControl.component;
    }
  }];

  const controlWithDecorators = decorators.reduce(applyDecorators, control);

  container.appendChild(controlWithDecorators.element);
  controls.push(controlWithDecorators);
  rows.push({
    valueElement: controlWithDecorators,
    field: {
      ...field,
      validation: {
        ...field.validation,
        ...(type === TextInputType.Text && { pattern: TEXT_FIELD_REGEX })
      },
    },
  });

  const validation = createValidation(rows, formId, formName);
  container.appendChild(validation.element);

  return {
    row: container,
    controls,
    validation
  };
}

export const text = (field: any, formId: string, formName: string): FormElement => createInput(field, TextInputType.Text, formId, formName);

export const password = (field: any, formId: string, formName: string): FormElement => createInput(field, TextInputType.Password, formId, formName);

export const email = (field: any, formId: string, formName: string): FormElement => createInput(field, TextInputType.Email, formId, formName);

export const hidden = (field: any, formId: string, formName: string): FormElement => createInput(field, TextInputType.Hidden, formId, formName);

export const selectControl = (field: any, prefixControl = false) => {
  const isSelected = (opt: any) => {
    if (opt.value === field.value) {
      return true;
    }

    return false;
  }

  const options: Array<{ value: string, text: string }> = field.options.map((opt: any) => ({
    value: opt.value,
    text: translate(opt.value ? `field.${field.name}.listMap.${opt.value}` : `field.${field.name}`),
    selected: isSelected(opt)
  }));

  return new SelectElement(field.name,
    field.classList
      || prefixControl
      ? ["columns", "four", "dob", "form-field", "form-field-select"]
      : ["form-field", "form-field-select"],
    options);
}

export const select = (field: any, formId: string, formName: string): FormElement => {
  const container = document.createElement("div");
  container.setAttribute("class", "input-container");

  const { element: label } = createLabel(field, ["label-visible"]);
  const control = selectControl(field);
  container.appendChild(label);
  container.appendChild(control.element);

  const validation = createValidation([{ valueElement: control, field }], formId, formName);
  container.appendChild(validation.element);

  return {
    row: container,
    controls: [control],
    validation
  };
}

export const dateselect = (field: any, formId: string, formName: string): FormElement => {
  const getUserValue = () => field.value || ""

  const container = document.createElement("div");
  container.classList.add(...["input-container", "row"]);

  const { element: label } = createLabel(field, ["label-visible"]);
  const control = new NumericDateElement(field.name, getUserValue(), field.classList || ["dob", "four", "columns"], field.validation?.minYear);

  while (control.element.firstChild) {
    container.appendChild(control.element.children[0]);
  }

  container.appendChild(label);

  const validation = createValidation([{ valueElement: control, field }], formId, formName);
  container.appendChild(validation.element);

  return {
    row: container,
    controls: [control],
    validation
  };
}

const marketingConsentUnlinkedFields = [
  "communicationOptins_dahadaha_email",
  "communicationOptins_dahadaha_sms",
  "communicationOptins_dahadaha_phone",
];

// The dahadaha comm prefs are independent of marketing consent ONLY in the
// registration stepper (AB#2871830). Everywhere else (notably
// editProfileForm) deselecting marketing consent must deselect and hide the
// comm preference checkboxes.
const unlinkedFieldsFor = (formName: string) =>
  formName === "registrationForm" ? marketingConsentUnlinkedFields : [];

export const checkbox = (field: any, formId: string, formName: string): FormElement => {
  const container = document.createElement("div");
  container.classList.add(...["input-container"]);

  const containerLabel = document.createElement("label");
  containerLabel.classList.add(...["optin"]);

  const { element: label } = createLabel(field, ["label-checkbox"]);
  const checkboxElement = new CheckboxElement(field.name, field.value ? field.value : false, field.classList || ["checkmark"]);

  containerLabel.appendChild(checkboxElement.element);
  containerLabel.appendChild(label);
  container.appendChild(containerLabel);
  const decorators: any = [{
    canApply: field.name === "marketingConsentRegistration",
    apply: (ctrl: Instance) => new MarketingConsentDecorator(ctrl, unlinkedFieldsFor(formName))
  }, {
    canApply: field.name === "personalizationConsentRegistration" || formName !== "postLoginForm" && field.name.includes("communicationOptins_") && !unlinkedFieldsFor(formName).includes(field.name),
    apply: (ctrl: Instance) => new HideCheckboxDecorator(ctrl)
  }, {
    canApply: field.name.startsWith("checkboxAbroadConsent"),
    apply: (ctrl: Instance) => new AbroadConsentDecorator(ctrl)
  }, {
    canApply: field.name.startsWith("rejectAbroadConsent"),
    apply: (ctrl: Instance) => new AbroadConsentConfirmDecorator(ctrl)
  }];

  decorators.reduce(applyDecorators, checkboxElement);

  const validation = createValidation([{ valueElement: checkboxElement, field }], formId, formName);
  container.appendChild(validation.element);

  return {
    row: container,
    controls: [checkboxElement],
    validation
  };
}

export const paragraph = (field: any): { row: HTMLElement; controls: ValueComponent[]; validation?: Validation } => {
  const p = document.createElement("p");
  p.className = "ces-form-paragraph";
  if (field.text) {
    p.innerHTML = DOMPurify.sanitize(field.text);
  }
  return { row: p, controls: [] };
};

export const captcha = (field: any, formId: string, formName: string): FormElement => {
    const container = document.createElement("div");
    container.classList.add("recaptcha-container");

    // Set version and site key from field config
    if (field.version) {
        CESAppInfo.reCaptchaVer = field.version;
    }
    if (field.siteKey) {
        CESAppInfo.reCaptchakey = field.siteKey;
    }

    // Initialize reCAPTCHA with form ID
    const control = RecaptchaFactory.getInstance(formId, field.name);
    
    if (!control) {
        console.error("Failed to initialize reCAPTCHA control");
        // Return a minimal form element to maintain type safety
        return {
            row: container,
            controls: [],
            validation: createValidation([], formId, formName)
        };
    }

    // Ensure element exists and is added only once
    if (control.element && !container.contains(control.element)) {
        container.appendChild(control.element);
    }

    const validation = createValidation([{ valueElement: control, field }], formId, formName);
    container.appendChild(validation.element);

    return {
        row: container,
        controls: [control],
        validation
    };
}

export const submit = (formId: string, formName: string, onSubmit: Function) => {
  const { element: control } = new ButtonElement(
    `ces-form-submit-${formId}`,
    translate(`i18nStrings.${formName}.buttonSubmit`),
    ["ces-form-submit", "button-primary"],
    onSubmit
  );

  return control;
}

export const nextBtn = () => {
  const { element: control } = new ButtonElement(
    'next',
    translate(`i18nStrings.buttonNext`) || 'Next',
    ["button-primary"],
    () => { },
  );
  return control;
}

export const prevBtn = () => {
  const { element: control } = new ButtonElement(
    'prev',
    translate(`i18nStrings.buttonPrev`) || 'Previous',
    ["button-primary"],
    () => { },
  );
  return control;
}